<template>
  <v-container fluid>
    <v-row>
      <!-- Основная информация -->
      <v-col cols="12">
        <h3 class="mb-3">{{ $t('taxi.vehicles.taxi_data') }}</h3>
      </v-col>

      <!-- Класс автомобиля -->
      <v-col cols="12" sm="6" md="4">
        <v-card outlined>
          <v-card-text>
            <div class="overline mb-2">{{ $t('taxi.vehicle_classes.vehicle_class') }}</div>
            <v-chip :color="getClassColor(car.taxi_vehicle_class)" dark>
              <v-icon left small>{{ getClassIcon(car.taxi_vehicle_class) }}</v-icon>
              {{ $t(`taxi.vehicle_classes.${car.taxi_vehicle_class || 'econom'}`) }}
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Разрешение на такси -->
      <v-col cols="12" sm="6" md="4">
        <v-card outlined>
          <v-card-text>
            <div class="overline mb-2">{{ $t('taxi.vehicles.permit_number') }}</div>
            <div v-if="car.taxi_permit_number" class="text-h6">
              {{ car.taxi_permit_number }}
            </div>
            <div v-else class="grey--text">
              {{ $t('common.not_specified') }}
            </div>
            <div v-if="car.taxi_permit_expires_at" class="caption mt-1">
              <v-icon :color="permitExpiryColor" x-small class="mr-1">
                mdi-calendar-clock
              </v-icon>
              {{ $t('common.valid_until') }}: {{ formatDate(car.taxi_permit_expires_at) }}
              <v-chip v-if="isPermitExpired" color="error" x-small class="ml-2">
                {{ $t('taxi.driver_documents.expired') }}
              </v-chip>
              <v-chip v-else-if="isPermitExpiringSoon" color="warning" x-small class="ml-2">
                {{ $t('taxi.driver_documents.expires_soon') }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- ID в Яндекс.Такси -->
      <v-col cols="12" sm="6" md="4">
        <v-card outlined>
          <v-card-text>
            <div class="overline mb-2">{{ $t('taxi.yandex_integration.yandex_vehicle_id') }}</div>
            <div v-if="car.yandex_vehicle_id" class="text-h6">
              {{ car.yandex_vehicle_id }}
              <v-icon color="success" small class="ml-1">mdi-check-circle</v-icon>
            </div>
            <div v-else class="grey--text">
              {{ $t('common.not_connected') }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Статистика -->
    <v-row class="mt-4" v-if="showStatistics">
      <v-col cols="12">
        <h3 class="mb-3">{{ $t('taxi.analytics.statistics') }}</h3>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card outlined>
          <v-card-text class="text-center">
            <div class="text-h4">{{ statistics.totalShifts || 0 }}</div>
            <div class="caption grey--text">{{ $t('taxi.analytics.total_shifts') }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card outlined>
          <v-card-text class="text-center">
            <div class="text-h4">{{ statistics.activeShifts || 0 }}</div>
            <div class="caption grey--text">{{ $t('taxi.shifts.active_shifts') }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card outlined>
          <v-card-text class="text-center">
            <div class="text-h4">{{ formatCurrency(statistics.totalEarnings || 0) }}</div>
            <div class="caption grey--text">{{ $t('taxi.analytics.total_earnings') }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card outlined>
          <v-card-text class="text-center">
            <div class="text-h4">{{ formatCurrency(statistics.averageDailyEarnings || 0) }}</div>
            <div class="caption grey--text">{{ $t('taxi.shifts.average_daily_earnings') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Последние смены -->
    <v-row class="mt-4" v-if="recentShifts.length > 0">
      <v-col cols="12">
        <h3 class="mb-3">{{ $t('taxi.shifts.recent_shifts') }}</h3>
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th>{{ $t('custom.date') }}</th>
                <th>{{ clientLabel }}</th>
                <th>{{ $t('taxi.shifts.shift_duration') }}</th>
                <th>{{ $t('taxi.shifts.total_rides') }}</th>
                <th>{{ $t('taxi.shifts.driver_earnings') }}</th>
                <th>{{ $t('custom.status') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="shift in recentShifts" :key="shift.id">
                <td>{{ formatDate(shift.shift_start) }}</td>
                <td>{{ shift.driver_name }}</td>
                <td>{{ shift.duration_hours }} {{ $t('custom.hours') }}</td>
                <td>{{ shift.total_rides || 0 }}</td>
                <td>{{ formatCurrency(shift.driver_earnings || 0) }}</td>
                <td>
                  <v-chip :color="getStatusColor(shift.shift_status)" dark x-small>
                    {{ $t(`taxi.shifts.${shift.shift_status}_shift`) }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <div class="text-center mt-3">
          <v-btn text color="primary" @click="viewAllShifts">
            {{ $t('common.view_all') }}
            <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Интеграция с Яндекс -->
    <v-row class="mt-4" v-if="car.yandex_vehicle_id">
      <v-col cols="12">
        <v-card outlined>
          <v-card-text>
            <v-row align="center">
              <v-col>
                <div class="d-flex align-center">
                  <v-icon color="warning" class="mr-2">mdi-taxi</v-icon>
                  <div>
                    <div class="font-weight-medium">{{ $t('taxi.yandex_integration.integration_status') }}</div>
                    <div class="caption grey--text">
                      {{ $t('taxi.yandex_integration.last_sync') }}:
                      {{ car.last_yandex_sync ? formatDateTime(car.last_yandex_sync) : $t('common.never') }}
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col class="text-right">
                <v-btn color="primary" outlined small @click="syncWithYandex" :loading="syncing">
                  <v-icon left small>mdi-sync</v-icon>
                  {{ $t('taxi.yandex_integration.sync_now') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'

export default {
  name: 'TaxiVehicleInfo',
  props: {
    car: {
      type: Object,
      required: true
    },
    showStatistics: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: false,
      syncing: false,
      statistics: {
        totalShifts: 0,
        activeShifts: 0,
        totalEarnings: 0,
        averageDailyEarnings: 0
      },
      recentShifts: []
    }
  },
  computed: {
    ...mapGetters('session_manager', ['company']),

    isPermitExpired() {
      if (!this.car.taxi_permit_expires_at) return false
      return moment().isAfter(moment(this.car.taxi_permit_expires_at))
    },

    isPermitExpiringSoon() {
      if (!this.car.taxi_permit_expires_at) return false
      const daysUntilExpiry = moment(this.car.taxi_permit_expires_at).diff(moment(), 'days')
      return daysUntilExpiry > 0 && daysUntilExpiry <= 30
    },

    permitExpiryColor() {
      if (this.isPermitExpired) return 'error'
      if (this.isPermitExpiringSoon) return 'warning'
      return 'success'
    }
  },
  mounted() {
    if (this.showStatistics) {
      this.fetchStatistics()
      this.fetchRecentShifts()
    }
  },
  methods: {
    async fetchStatistics() {
      try {
        const response = await this.$axios.get(`/api/v1/taxi/analytics/vehicle_statistics`, {
          params: { car_id: this.car.id }
        })
        this.statistics = response.data
      } catch (error) {
        console.error('Error fetching vehicle statistics:', error)
      }
    },

    async fetchRecentShifts() {
      try {
        const response = await this.$axios.get('/api/v1/taxi/shifts', {
          params: {
            car_id: this.car.id,
            limit: 5,
            order: 'desc'
          }
        })
        this.recentShifts = response.data.shifts || []
      } catch (error) {
        console.error('Error fetching recent shifts:', error)
      }
    },

    async syncWithYandex() {
      this.syncing = true
      try {
        await this.$axios.post(`/api/v1/taxi/yandex/sync_vehicle`, {
          car_id: this.car.id
        })
        this.$notifier.showMessage({
          content: this.$t('taxi.yandex_integration.sync_started'),
          color: 'success'
        })
        this.$emit('synced')
      } catch (error) {
        console.error('Error syncing with Yandex:', error)
        this.$notifier.showMessage({
          content: this.$t('common.error_occurred'),
          color: 'error'
        })
      } finally {
        this.syncing = false
      }
    },

    viewAllShifts() {
      this.$router.push({
        name: 'TaxiShifts',
        query: { car_id: this.car.id }
      })
    },

    getClassColor(vehicleClass) {
      const colors = {
        econom: 'blue-grey',
        comfort: 'blue',
        comfort_plus: 'indigo',
        business: 'deep-purple',
        premium: 'purple'
      }
      return colors[vehicleClass] || 'grey'
    },

    getClassIcon(vehicleClass) {
      const icons = {
        econom: 'mdi-car-side',
        comfort: 'mdi-car-estate',
        comfort_plus: 'mdi-car-sports',
        business: 'mdi-car-limousine',
        premium: 'mdi-car-convertible'
      }
      return icons[vehicleClass] || 'mdi-car'
    },

    getStatusColor(status) {
      const colors = {
        pending: 'blue-grey',
        active: 'green',
        completed: 'deep-purple',
        cancelled: 'red'
      }
      return colors[status] || 'grey'
    },

    formatDate(date) {
      return moment(date).format('DD.MM.YYYY')
    },

    formatDateTime(date) {
      return moment(date).format('DD.MM.YYYY HH:mm')
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount)
    }
  }
}
</script>

<style scoped>
.v-simple-table {
  font-size: 14px;
}
</style>
